/* ==========================================================================
   IRON FIT — Machine page controller

   Spin viewer: commercial-360 style. Frames are SWAPPED, never blended —
   one image visible at a time, driven by a continuous rotation value with
   drag, flick inertia and eased button steps, all on requestAnimationFrame.
   Smoothness scales with frame count: drop more photos into the frames
   array (or raise `count` on a pattern) and the same viewer runs at the
   density of a tenten-style turntable. No other change needed.

   Add-ons: the accessories the old store sold with machines — bench, 7 ft
   bar and rubber plates per kg — picked with big toggles, summed
   into a live total, and written into the WhatsApp order message.
   ========================================================================== */
(function () {
  'use strict';


  /* Accessory prices from the live store's own addon feed (products.json):
     bar $120 · bench $180 · rubber plates $3/kg. */
  var ADDONS = {
    bench: { name: 'Commercial Adjustable Bench', price: 180, img: 'assets/04-commercial-adjustable-bench-180-delivered-760.jpg' },
    bar:   { name: '7 FT Olympic Bar + Collars',  price: 120, img: 'assets/05-olympic-bar-120-delivered-760.jpg' }
  };
  var PLATE_PER_KG = 3;      /* standalone rate — $2.5/kg is the package rate */

  /* Rubber flooring is priced by the square metre and laid in 50 × 50 cm
     tiles, so a square metre is four tiles at $9.75 each. Tiles are not sold
     loose: the smallest order is one full square metre, which is those four
     tiles for $39. That minimum lives in IFZFloor rather than in a check
     here, so it cannot be stepped past. */
  var FLOOR_PER_M2 = 39;

  var PRODUCTS = {
    compact: {
      kicker: 'Small space',
      name: 'Compact Single-Stack Gym',
      price: 550,
      frames: { pattern: 'assets/spin-compact-{n}-1040.jpg', count: 1 },
      promise: 'The price covers <b>the machine and our team installing and testing it in your room</b>.' + " Delivery is <b>$50 to $100</b> on top, set by your region and charged once for the whole order.",
      addons: ['bench', 'bar'],
      features: [
        'Smallest footprint we sell — 94 × 100 cm, under 1 m² of floor',
        'Built-in weight stack — no loose plates to buy or store',
        'Dips, pull-ups, lat pulldowns, low rows and cable exercises',
        'Made for apartments and small rooms'
      ],
      included: [
        'The machine',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench, Olympic bar and plates — add them below if you want them'],
      dims: [{ v: '226 cm', k: 'Height' }, { v: '94 cm', k: 'Width' }, { v: '100 cm', k: 'Depth' }],
      /* Tripo export, 57 MB as it came. Fine over localhost, far too heavy to
         ship — it needs decimating before this goes anywhere near live. */
      model: 'assets/ifz-compact-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '226 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '94 cm',  a: [0.16, 0.02, 0.50], b: [0.84, 0.02, 0.50] },
        depth:  { l: '100 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Lat pulldown bar',      p: [0.50, 0.96, 0.50] },
        { l: 'Built-in weight stack', p: [0.52, 0.34, 0.42] },
        { l: 'Leg rollers',           p: [0.74, 0.22, 0.34] }
      ],
      others: ['crossover', 'smith', 'bench']
    },
    crossover: {
      kicker: 'Best seller',
      name: 'Dual Cable Crossover',
      price: 760,
      frames: { pattern: 'assets/spin-cable-{n}.jpg', count: 4 },
      promise: 'The price covers <b>the machine, a 7 ft Olympic bar with collars, and our team installing and testing it</b>.' + " Delivery is <b>$50 to $100</b> on top, set by your region and charged once for the whole order.",
      addons: ['bench'],                     /* bar already included */
      features: [
        'Two separate 75 kg weight stacks — train both sides',
        'Two cable stations you can set to any height',
        'Pull-up bar with several grip positions',
        'Heavy-duty steel frame, powder-coated'
      ],
      included: [
        'The machine',
        'One 7 ft Olympic bar with collars',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench and plates — add them below if you want them', 'This is the cable machine — it has no Smith bar'],
      dims: [{ v: '210 cm', k: 'Height' }, { v: '150 cm', k: 'Width' }, { v: '120 cm', k: 'Depth' }, { v: '2 × 75 kg', k: 'Weight stacks' }],
      model: 'assets/ifz-crossover-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.06, 0.02, 0.50], b: [0.94, 0.02, 0.50] },
        depth:  { l: '120 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Multi-grip pull-up bar', p: [0.50, 0.97, 0.50] },
        { l: '2 × 75 kg stacks',       p: [0.42, 0.30, 0.42] },
        { l: 'Adjustable pulleys',     p: [0.14, 0.70, 0.60] }
      ],
      others: ['compact', 'smith', 'plates']
    },
    smith: {
      kicker: 'Complete system',
      name: 'Multifunctional Smith Machine',
      price: 1059,
      frames: { pattern: 'assets/spin-smith-{n}.jpg', count: 5 },
      promise: 'The price covers <b>the machine and our team installing and testing it in your room</b>.' + " Delivery is <b>$50 to $100</b> on top, set by your region and charged once for the whole order.",
      addons: ['bench', 'bar'],
      features: [
        'Guided Smith bar built into the machine',
        'Two cable stations with adjustable pulleys',
        'J-hooks for free-bar work and storage horns for your plates',
        'One frame that covers every big lift'
      ],
      included: [
        'The machine, with its built-in Smith bar',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench and plates — add them below if you want them', 'No separate loose 7 ft bar — the Smith bar is part of the machine'],
      dims: [{ v: '210 cm', k: 'Height' }, { v: '150 cm', k: 'Width' }, { v: '118 cm', k: 'Depth' }],
      model: 'assets/ifz-smith-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.04, 0.02, 0.50], b: [0.96, 0.02, 0.50] },
        depth:  { l: '118 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Guided Smith bar',   p: [0.50, 0.72, 0.30] },
        { l: 'Cable stations',     p: [0.10, 0.66, 0.60] },
        { l: 'J-hooks & horns',    p: [0.88, 0.40, 0.60] }
      ],
      others: ['compact', 'crossover', 'bench']
    },

    /* ------------------------------------------------------------------
       Accessories. Same template, same spin viewer. `accessory: true` means
       the page drops the add-on picker (you don't bolt a bench onto a bench)
       and swaps "See it spin & order" language for a plain delivered price.
       ------------------------------------------------------------------ */
    bench: {
      accessory: true,
      kicker: 'Bench',
      name: 'Commercial Adjustable Bench',
      price: 180,
      frames: { pattern: 'assets/spin-bench-{n}.jpg', count: 5 },
      promise: 'The bench is <b>$180</b>, plus <b>$20</b> delivery. One delivery charge per order at the dearest rate in it, so a bench and a socket set together is still $20 — and cash at the door, as always.',
      addons: [],
      features: [
        'Adjusts from flat to full upright',
        'Commercial-grade padding and steel frame',
        'Leg anchor for decline and sit-up work',
        'Works with every Iron Fit machine'
      ],
      included: ['The bench'],
      notIncluded: ['$20 delivery — one charge per order, at the dearest rate in it', 'No installation needed — it arrives ready to use'],
      dims: [{ v: 'Flat to upright', k: 'Backrest' }],
      others: ['bar', 'plates']
    },

    bar: {
      accessory: true,
      kicker: 'Barbell',
      name: '7 FT Olympic Bar + Collars',
      price: 120,
      frames: { pattern: 'assets/spin-bar-{n}.jpg', count: 5 },
      promise: 'The bar and its collars are <b>$120</b>, plus <b>$20</b> delivery. One delivery charge per order at the dearest rate in it — and cash at the door, as always.',
      addons: [],
      features: [
        'Full 7 ft Olympic length',
        'Comes with a pair of collars',
        'Knurled grip, rotating sleeves',
        'Fits all standard Olympic plates'
      ],
      included: ['The 7 ft Olympic bar', 'A pair of collars'],
      notIncluded: ['$20 delivery — one charge per order, at the dearest rate in it', 'Plates are sold separately — $3 per kg'],
      dims: [{ v: '7 ft', k: 'Length' }],
      others: ['bench', 'plates']
    },

    plates: {
      accessory: true,
      perKg: true,
      kicker: 'Plates',
      name: 'Rubber Olympic Plates',
      price: 3,
      frames: { pattern: 'assets/spin-plates-{n}.jpg', count: 4 },
      promise: 'Rubber plates are <b>$3 per kg, delivered</b>. Buy exactly the weight you want — pick it below and the total works itself out.',
      addons: [],
      features: [
        'Rubber coated — quiet, and kind to your floor',
        'Standard Olympic centre, fits any 7 ft bar',
        'Available in 5, 10 and 20 kg plates',
        'Bought by the kilo — pay only for what you take'
      ],
      included: ['The plates you choose'],
      notIncluded: ['$20 delivery — one charge per order, at the dearest rate in it', 'Bar sold separately — $120 with collars'],
      dims: [{ v: '5 / 10 / 20 kg', k: 'Plate sizes' }, { v: '$3', k: 'Per kg' }],
      others: ['bar', 'bench']
    },

    /* Sold by the square metre, the way a floor is measured, rather than by
       the tile — but the tile is what arrives, so both numbers are on the
       page. `perM2` is the flooring twin of `perKg`: the picker is the
       product, and the price shown is the rate. */
    floor: {
      accessory: true,
      perM2: true,
      kicker: 'Flooring',
      name: 'Rubber Gym Flooring — 15 mm solid',
      price: 39,
      photoLabel: 'Photo of the real tiles',
      frames: { pattern: 'assets/floor-15mm-760.jpg', count: 1 },
      promise: 'Flooring is <b>$39 per m², delivered</b> — one square metre is four 50 × 50 cm tiles, so that is <b>$9.75 a tile</b>. Pick the area below and the total works itself out. Four tiles — one square metre — is the smallest order.',
      addons: [],
      features: [
        'Tiles are 50 × 50 cm and 15 mm thick — four of them make one square metre',
        'Solid rubber, 3 kg a tile, so it stays where you put it without glue',
        'Takes a dropped barbell and a 200 kg stack without marking the floor underneath',
        'Cuts the noise a plate makes when it lands, which is what the neighbours hear'
      ],
      included: ['The tiles you choose'],
      notIncluded: ['$20 delivery — one charge per order, at the dearest rate in it', 'No installation — they lay straight onto a flat floor', 'Four tiles — one square metre — is the smallest order'],
      dims: [{ v: '50 × 50 cm', k: 'Tile' }, { v: '15 mm', k: 'Thick' }, { v: '3 kg', k: 'Each' }],
      others: ['bench', 'bar', 'plates']
    },

    /* ------------------------------------------------------------------
       THE TOOL KITS

       They are accessories as far as this page is concerned — one photo, no
       installation, no addons — but they are the only things on the site
       somebody buys with nobody turning up to explain them. So the useful
       detail is the detail a person standing in a shop would check: what
       drive the sockets are, what range they cover, what the bits fit.

       `dims` is the three-box row under the features, which is why it holds
       pieces / drive / range rather than height / width / depth.

       Everything written here is read off the kits' own photographs. Where
       a number is not legible in them it is left out — a wrong socket size
       is a refused delivery and a wasted trip across Lebanon, which costs
       more than a $5 kit earns.
       ------------------------------------------------------------------ */
    'tool-socket46': {
      accessory: true,
      cartType: 'tool',
      /* the case, then the contents, then it in use */
      gallery: [
        'assets/tool-socket46-g2-760.jpg',
        'assets/tool-socket46-g3-760.jpg',
        'assets/tool-socket46-g4-760.jpg',
        'assets/tool-socket46-g5-760.jpg'
      ],
      kicker: 'Socket set',
      name: '46-Piece Socket Set',
      price: 5,
      photoLabel: 'Photo of the real kit',
      frames: { pattern: 'assets/tool-socket46-red-2-760.jpg', count: 1 },
      promise: 'The kit is <b>$5</b>, plus <b>$4</b> delivery. One delivery charge per order at the dearest rate in it, so add a bench and the whole order is $20 rather than $24. Cash at the door either way.',
      addons: [],
      features: [
        'Sockets from 4 to 14 mm on a 1/4" drive — the range most car, bike and furniture bolts live in',
        'Bit sockets for slotted, Phillips, hex and Torx heads',
        'Quick-release ratchet, spinner handle, sliding T-bar, extension bars and a universal joint',
        'Mirror-polished chrome vanadium'
      ],
      included: ['The 46-piece set in its red case', 'Delivery to your door — all Lebanon'],
      notIncluded: ['$4 delivery — one charge per order, at the dearest rate in it', 'Nothing to install — it arrives ready to use'],
      dims: [{ v: '46', k: 'Pieces' }, { v: '1/4"', k: 'Drive' }, { v: '4–14 mm', k: 'Sockets' }],
      others: ['tool-box53', 'tool-pro108', 'tool-precision115']
    },

    'tool-precision115': {
      accessory: true,
      cartType: 'tool',
      /* the case, then the contents, then it in use */
      gallery: [
        'assets/tool-precision115-g2-760.jpg',
        'assets/tool-precision115-g3-760.jpg',
        'assets/tool-precision115-g4-760.jpg'
      ],
      kicker: 'Precision set',
      name: '115-in-1 Precision Screwdriver Set',
      price: 8,
      photoLabel: 'Photo of the real kit',
      frames: { pattern: 'assets/tool-precision115-black-2-760.jpg', count: 1 },
      promise: 'The kit is <b>$8</b>, plus <b>$4</b> delivery. One delivery charge per order at the dearest rate in it, so add a bench and the whole order is $20 rather than $24. Cash at the door either way.',
      addons: [],
      features: [
        '115 hardened chrome-vanadium bits for phones, laptops, consoles, watches and glasses',
        'Driver with a non-slip grip and a rotating end cap — press with the palm, turn with one finger',
        'Flexible shaft, rigid extension and a right-angle adapter for screws you cannot get straight at',
        'Spudgers, a suction cup and tweezers, so a phone opens without marks on the case'
      ],
      included: ['The 115-piece set in its black case', 'Delivery to your door — all Lebanon'],
      notIncluded: ['$4 delivery — one charge per order, at the dearest rate in it', 'Nothing to install — it arrives ready to use'],
      dims: [{ v: '115', k: 'Pieces' }, { v: '4 mm', k: 'Bit shank' }, { v: 'CR-V', k: 'Steel' }],
      others: ['tool-repair128', 'tool-socket46', 'tool-box53']
    },

    'tool-box53': {
      accessory: true,
      cartType: 'tool',
      /* the case, then the contents, then it in use */
      gallery: [
        'assets/tool-box53-g2-760.jpg',
        'assets/tool-box53-g3-760.jpg',
        'assets/tool-box53-g4-760.jpg',
        'assets/tool-box53-g5-760.jpg',
        'assets/tool-box53-g6-760.jpg',
        'assets/tool-box53-g7-760.jpg'
      ],
      kicker: 'Socket set',
      name: '53-Piece Socket & Tool Box',
      price: 12,
      photoLabel: 'Photo of the real kit',
      frames: { pattern: 'assets/tool-box53-orange-2-760.jpg', count: 1 },
      promise: 'The kit is <b>$12</b>, plus <b>$4</b> delivery. One delivery charge per order at the dearest rate in it, so add a bench and the whole order is $20 rather than $24. Cash at the door either way.',
      addons: [],
      features: [
        '53 pieces — sockets, bit sockets, extensions, a screwdriver handle and a sliding T-bar',
        '24-tooth ratchet: 15° between clicks, so it keeps turning where a coarse ratchet jams',
        'Spark plug socket included',
        'Orange carry case with a handle and two latches'
      ],
      included: ['The 53-piece set in its orange case', 'Delivery to your door — all Lebanon'],
      notIncluded: ['$4 delivery — one charge per order, at the dearest rate in it', 'Nothing to install — it arrives ready to use'],
      dims: [{ v: '53', k: 'Pieces' }, { v: '1/4"', k: 'Drive' }, { v: '24', k: 'Ratchet teeth' }],
      others: ['tool-socket46', 'tool-pro108', 'tool-repair128']
    },

    'tool-repair128': {
      accessory: true,
      cartType: 'tool',
      /* the case, then the contents, then it in use */
      gallery: [
        'assets/tool-repair128-g2-760.jpg',
        'assets/tool-repair128-g3-760.jpg',
        'assets/tool-repair128-g4-760.jpg',
        'assets/tool-repair128-g5-760.jpg',
        'assets/tool-repair128-g6-760.jpg',
        'assets/tool-repair128-g7-760.jpg'
      ],
      kicker: 'Precision set',
      name: '128-in-1 Precision Bit Set',
      price: 15,
      photoLabel: 'Photo of the real kit',
      frames: { pattern: 'assets/tool-repair128-grey-2-760.jpg', count: 1 },
      promise: 'The kit is <b>$15</b>, plus <b>$4</b> delivery. One delivery charge per order at the dearest rate in it, so add a bench and the whole order is $20 rather than $24. Cash at the door either way.',
      addons: [],
      features: [
        'Phillips, Pozi, slotted, hex, Torx, security Torx, tri-wing, square, triangle and U-shape bits',
        'Hex in metric and imperial, so American kit is covered as well as everything else',
        'Magnetiser and demagnetiser pad built into the case — magnetise a bit and the screw comes out with it',
        'Flexible shaft, rigid extension, tweezers, pry tools and mini open-end spanners',
        'Folds down to 16 × 7.5 cm — the whole set closes into one hand'
      ],
      included: ['The 128-piece set in its fold-out case', 'Delivery to your door — all Lebanon'],
      notIncluded: ['$4 delivery — one charge per order, at the dearest rate in it', 'Nothing to install — it arrives ready to use'],
      dims: [{ v: '128', k: 'Pieces' }, { v: '16 × 7.5 cm', k: 'Closed' }, { v: '4 mm', k: 'Bit shank' }],
      others: ['tool-precision115', 'tool-box53', 'tool-pro108']
    },

    'tool-pro108': {
      accessory: true,
      cartType: 'tool',
      /* the case, then the contents, then it in use */
      gallery: [
        'assets/tool-pro108-g2-760.jpg',
        'assets/tool-pro108-g3-760.jpg'
      ],
      kicker: 'Car repair set',
      name: '108-Piece Car Repair Set',
      price: 45,
      photoLabel: 'Photo of the real kit',
      frames: { pattern: 'assets/tool-pro108-blue-2-760.jpg', count: 1 },
      promise: 'The kit is <b>$45</b>, plus <b>$4</b> delivery. One delivery charge per order at the dearest rate in it, so add a bench and the whole order is $20 rather than $24. Cash at the door either way.',
      addons: [],
      features: [
        'Two drives in one case — 1/2" for wheel nuts and suspension, 1/4" for everything small',
        'Sockets from 4 to 32 mm, in both short and deep',
        'E10 to E24 star sockets — the ones European cars need and most kits leave out',
        'Spark plug sockets in 16 and 21 mm',
        'Two ratchets, universal joints, extension bars, sliding T-bars and hex keys',
        'Blue heavy-duty case with a carry handle'
      ],
      included: ['The 108-piece set in its blue case', 'Delivery to your door — all Lebanon'],
      notIncluded: ['$4 delivery — one charge per order, at the dearest rate in it', 'Nothing to install — it arrives ready to use'],
      dims: [{ v: '108', k: 'Pieces' }, { v: '1/4" + 1/2"', k: 'Drive' }, { v: '4–32 mm', k: 'Sockets' }],
      others: ['tool-box53', 'tool-socket46', 'tool-repair128']
    }
  };

  function $(s) { return document.querySelector(s); }
  function money(n) { return '$' + n.toLocaleString('en-US'); }

  /* The cart shows a photo of every line now. Every product here is already
     described by its spin frames, so frame 1 is the thumbnail — no second
     list of images to keep in step with the first. */
  function thumbOf(p) {
    if (!p || !p.frames || !p.frames.pattern) return '';
    return window.IFZA(p.frames.pattern.replace('{n}', '1'));
  }

  /* Which product this page is.

     Static site: the ?m= parameter.
     Shopify: there is no such parameter — the theme knows which product it
     is rendering and tells us, because a handle is the only thing that
     survives a product being renamed. See sections/ifz-product.liquid. */
  function key() {
    if (window.IFZ_PRODUCT_KEY && PRODUCTS[window.IFZ_PRODUCT_KEY]) {
      return window.IFZ_PRODUCT_KEY;
    }
    var m = new URLSearchParams(location.search).get('m');
    return PRODUCTS[m] ? m : 'crossover';
  }

  function frameList(f) {
    var out = [];
    for (var i = 1; i <= f.count; i++) out.push(f.pattern.replace('{n}', i));
    return out;
  }

  /* ------------------------------------------------------------------
     Order state → total → WhatsApp message
     ------------------------------------------------------------------ */
  var state = { picked: {}, plates: 0, floor: 0 };

  function total(p) {
    /* Plates and flooring are the product itself on their own pages, not an
       add-on: the price IS the rate, per kg or per square metre. */
    if (p.perKg) return state.plates * p.price;
    if (p.perM2) return state.floor * p.price;
    var t = p.price;
    p.addons.forEach(function (k) { if (state.picked[k]) t += ADDONS[k].price; });
    t += state.plates * PLATE_PER_KG;
    t += state.floor * FLOOR_PER_M2;
    return t;
  }

  var barSync = null;              /* set once the phone buy bar is built */

  /* There is no WhatsApp order link on this page any more, and the header
     icon is a plain "message us" — it used to be quietly rewritten to carry
     the whole cart as a pre-typed order. Orders go through the cash-on-
     delivery button only, because that is the only path the Pixel sees. */
  function refreshOrder(p) {
    $('#totalN').textContent = money(total(p));
    if (barSync) barSync();
  }

  /* ------------------------------------------------------------------
     Phone buy bar

     The spin viewer is the whole first screen on a phone, which put the
     price and "Add to cart" two screens below the fold. A pinned bar
     carries them instead — the same one the packages have.

     It does not own any state: it reads the total that refreshOrder just
     wrote, and it buys by clicking the page's own button, so the weight
     warning and the added state come along for free. It steps out of the
     way once the real order block is on screen, so there is never a pair
     of Add-to-cart buttons on the same screen.
     ------------------------------------------------------------------ */
  function buyBar(p) {
    var cart = document.getElementById('addCart');
    var order = document.getElementById('orderBox') || (cart && cart.closest('.order'));
    if (!cart) return;

    var bar = document.createElement('div');
    bar.className = 'buybar';
    bar.innerHTML =
      '<div class="buybar__tx"><b></b><div class="buybar__pr"><i></i><em>+ delivery</em></div></div>' +
      '<button class="buybar__cta" type="button">Add to cart</button>';
    bar.querySelector('b').textContent = p.name;
    document.body.appendChild(bar);
    /* the page's own bottom padding is added by class, so the pages that
       load this stylesheet without a buy bar keep their spacing */
    document.body.classList.add('has-buybar');

    var totalEl = bar.querySelector('i');
    var cta = bar.querySelector('.buybar__cta');

    barSync = function () {
      totalEl.textContent = $('#totalN').textContent;
      cta.classList.toggle('is-added', cart.classList.contains('is-added'));
      cta.classList.toggle('is-warn', cart.classList.contains('is-warn'));
      cta.textContent = cart.classList.contains('is-added') ? 'Added! View cart →'
                      : cart.classList.contains('is-warn') ? 'Pick a weight first'
                      : 'Add to cart';
    };

    cta.addEventListener('click', function () {
      cart.click();
      barSync();
      /* the weight warning puts itself back after 1.6s — follow it */
      setTimeout(barSync, 1700);
    });

    if (order && window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-away', entries[0].isIntersecting);
      }, { rootMargin: '-40px 0px -120px 0px' }).observe(order);
    }

    barSync();
  }

  /* ------------------------------------------------------------------
     Static content
     ------------------------------------------------------------------ */
  function fill(p) {
    document.title = 'Iron Fit — ' + p.name;
    if (window.IFZPixel) window.IFZPixel.viewContent(p.name, p.price);
    $('#kicker').textContent = p.kicker;
    $('#name').textContent = p.name;
    $('#price').textContent = p.perKg ? ('$' + p.price) : money(p.price);
    $('#promise').innerHTML = p.promise;
    inTheBox(p);

    /* Accessories are delivered, not installed, and plates are priced by the
       kilo — so the badge next to the price has to say the right thing. */
    var tag = document.querySelector('.price__tag');
    if (tag) {
      tag.textContent = p.perKg ? 'Per kg · delivered'
                     /* "$39" beside "per m²" gets read as one tile for $39,
                        so the badge says what the money actually buys */
                     : p.perM2 ? 'Per m² — that is 4 tiles'
                     /* the kits are the one thing here not quoted delivered */
                     : p.cartType === 'tool' ? '+ $4 delivery'
                     : p.accessory ? '+ $20 delivery'
                     : '+ delivery by region';
    }

    var f = $('#features'), inc = $('#included'), no = $('#notIncluded');
    p.features.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; f.appendChild(li); });
    p.included.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; inc.appendChild(li); });
    p.notIncluded.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; no.appendChild(li); });

    var dims = $('#dims');
    p.dims.forEach(function (d) {
      var s = document.createElement('span');
      s.textContent = d.v;
      var small = document.createElement('small');
      small.textContent = d.k;
      s.appendChild(small);
      dims.appendChild(s);
    });

    /* The machine page no longer carries this section — see ifz-product.liquid.
       Accessory pages still do, so this builds only when the row is there. */
    var row = $('#moreRow');
    if (!row) return;
    p.others.forEach(function (ok) {
      var o = PRODUCTS[ok];
      var a = document.createElement('a');
      a.className = 'more__card';
      a.href = 'machine.html?m=' + ok;
      var th = document.createElement('div'); th.className = 'more__thumb';
      /* a 74px square does not need the full studio master */
      var small = frameList(o.frames)[0].replace(/^(assets\/spin-[a-z]+-\d+)\.jpg$/i, '$1-760.jpg');
      th.appendChild(window.IFZPic(small, o.name));
      var div = document.createElement('div');
      var b = document.createElement('b'); b.textContent = o.name;
      var span = document.createElement('span');
      span.textContent = o.perKg ? ('$' + o.price + ' per kg')
                       : money(o.price) + (o.accessory ? ' + delivery' : ' + delivery');
      div.appendChild(b); div.appendChild(span);
      a.appendChild(th); a.appendChild(div);
      row.appendChild(a);
    });
  }


  /* ------------------------------------------------------------------
     WHAT COMES WITH IT, IN PICTURES

     The crossover ships with a 7 ft Olympic bar. That was written twice —
     in the promise line and in "the price includes" — and people still
     asked, because nobody reads a price page, they scan it. A photograph of
     the bar next to the word INCLUDED answers it at a glance in a way two
     more sentences never will.

     Driven off the machine's own `included` list rather than a hand-written
     list per product, so a machine that stops shipping with something stops
     showing it here too.
     ------------------------------------------------------------------ */
  var BOX_PICS = [
    { test: /olympic bar|7 ft bar|bar with collars/i,
      img: 'assets/spin-bar-1-760.jpg', name: '7 FT Olympic Bar', note: 'with collars' },
    { test: /adjustable bench/i,
      img: 'assets/spin-bench-1-760.jpg', name: 'Adjustable Bench', note: '' },
    { test: /rubber olympic plates|kg of plates/i,
      img: 'assets/spin-plates-1-760.jpg', name: 'Rubber Plates', note: '' }
  ];

  function inTheBox(p) {
    var host = $('#promise');
    if (!host || !p.included) return;

    var hits = BOX_PICS.filter(function (b) {
      return p.included.some(function (line) { return b.test.test(line); });
    });
    if (!hits.length) return;                 /* just a machine on its own */

    var box = document.createElement('div');
    box.className = 'inbox';
    box.innerHTML = '<span class="inbox__lead">Included with the machine — ' +
                    'no extra charge</span>';

    var row = document.createElement('div');
    row.className = 'inbox__row';

    hits.forEach(function (b) {
      var item = document.createElement('span');
      item.className = 'inbox__item';
      var media = document.createElement('span');
      media.className = 'inbox__pic';
      media.appendChild(window.IFZPic(b.img, b.name));
      var tx = document.createElement('span');
      tx.className = 'inbox__tx';
      tx.innerHTML = '<b>' + b.name + '</b>' + (b.note ? '<small>' + b.note + '</small>' : '');
      var tick = document.createElement('span');
      tick.className = 'inbox__tick';
      tick.textContent = '✓ Included';
      item.appendChild(media);
      item.appendChild(tx);
      item.appendChild(tick);
      row.appendChild(item);
    });

    box.appendChild(row);
    host.parentNode.insertBefore(box, host.nextSibling);
  }

  /* ------------------------------------------------------------------
     Add-ons UI
     ------------------------------------------------------------------ */
  function buildExtras(p) {
    var box = $('#extras');

    p.addons.forEach(function (k) {
      var a = ADDONS[k];
      var el = document.createElement('div');
      el.className = 'addon';
      el.setAttribute('role', 'checkbox');
      el.setAttribute('aria-checked', 'false');
      el.tabIndex = 0;

      var im = document.createElement('div'); im.className = 'addon__img';
      im.appendChild(window.IFZPic(a.img, a.name));

      var mid = document.createElement('div');
      var nm = document.createElement('div'); nm.className = 'addon__name'; nm.textContent = a.name;
      var pr = document.createElement('div'); pr.className = 'addon__price'; pr.textContent = '+ ' + money(a.price) + ' · delivered with the machine';
      mid.appendChild(nm); mid.appendChild(pr);

      var tick = document.createElement('div'); tick.className = 'addon__box';

      el.appendChild(im); el.appendChild(mid); el.appendChild(tick);

      function toggle() {
        state.picked[k] = !state.picked[k];
        el.classList.toggle('is-on', !!state.picked[k]);
        el.setAttribute('aria-checked', state.picked[k] ? 'true' : 'false');
        refreshOrder(p);
      }
      el.addEventListener('click', toggle);
      el.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); }
      });

      box.appendChild(el);
    });

    /* plates and flooring — the same pickers the packages use, at the
       standalone rate. On the plates or flooring page the picker IS the
       product, so it stays; on the bench / bar / tool pages there is nothing
       to add them to. */
    if (p.accessory && !p.perKg && !p.perM2) {
      var sec = box.closest('.extras');
      if (sec) sec.hidden = true;
      return;
    }

    /* The flooring page runs its own picker and nothing else — a floor has
       no plates to go with it. */
    if (p.perM2) {
      var fhead = document.createElement('p');
      fhead.className = 'pkc__exnote';
      fhead.innerHTML = 'Pick how many square metres you need. It is <b>$' + p.price +
        ' per m&sup2;</b> delivered, and one square metre is four 50 &times; 50 cm tiles ' +
        '(<b>$9.75</b> a tile). Four tiles is the smallest order.';
      box.appendChild(fhead);
      var fBox = document.createElement('div');
      box.appendChild(fBox);
      window.IFZFloor(fBox, function (m2Now) {
        state.floor = m2Now;
        refreshOrder(p);
      }, p.price);
      return;
    }

    var head = document.createElement('p');
    head.className = 'pkc__exnote';
    head.innerHTML = p.perKg
      ? 'Pick the weight you want in pairs of 5, 10 or 20 kg plates — up to 80 kg. ' +
        'Everything is <b>$' + p.price + ' per kg</b>, delivered to your door.'
      : 'Rubber Olympic plates are <b>$' + PLATE_PER_KG + ' per kg</b>, delivered with your ' +
        'machine on the same truck. Pick pairs of 5, 10 or 20 kg plates — up to 80 kg.';
    box.appendChild(head);

    var platesBox = document.createElement('div');
    box.appendChild(platesBox);

    window.IFZPlates(platesBox, function (kgNow) {
      state.plates = kgNow;
      refreshOrder(p);
    });

    /* Flooring, under the plates. It belongs on a machine page more than most
       add-ons do: the machine is what puts a 200 kg stack and a dropped
       barbell on somebody's tiles, and the installers are already in the room
       on delivery day. */
    var fNote = document.createElement('p');
    fNote.className = 'pkc__exnote';
    fNote.innerHTML = 'Rubber flooring is <b>$' + FLOOR_PER_M2 + ' per m&sup2;</b>, ' +
      'on the same truck as your machine. One square metre is ' +
      'four 50 &times; 50 cm tiles, 15 mm thick. Four tiles is the smallest order.';
    box.appendChild(fNote);

    var floorBox = document.createElement('div');
    box.appendChild(floorBox);

    window.IFZFloor(floorBox, function (m2Now) {
      state.floor = m2Now;
      refreshOrder(p);
    }, FLOOR_PER_M2);
  }

  /* ------------------------------------------------------------------
     Spin viewer — frame swap + inertia, rAF-driven
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     THE THUMBNAIL STRIP

     Buttons, not a scroller with dots: on a kit the photographs are not
     interchangeable — one is the case, one is the bit list, one is the
     ratchet close up — so being able to go straight to the one you want
     beats flicking past four to reach it.

     The big images are all in the stage already and only their visibility
     changes, which is the same swap the spin viewer does. Nothing is
     downloaded on a tap.
     ------------------------------------------------------------------ */
  function thumbs(stage, imgs, srcs, hint, p) {
    if (imgs.length < 2) return;

    var current = 0;
    var total = srcs.length;
    var strip = document.createElement('div');
    strip.className = 'gal';
    strip.setAttribute('role', 'tablist');
    strip.setAttribute('aria-orientation', 'horizontal');
    strip.setAttribute('aria-label', 'Photos of the ' + p.name);

    var btns = srcs.map(function (src, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gal__t' + (i === 0 ? ' is-on' : '');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-controls', 'stage');
      b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      b.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + total);
      b.tabIndex = i === 0 ? 0 : -1;
      b.appendChild(window.IFZPic(src, ''));
      var ti = b.querySelector('img');
      if (ti) { ti.loading = 'lazy'; ti.decoding = 'async'; }
      strip.appendChild(b);
      return b;
    });

    function keepThumbVisible(i) {
      var stripBox = strip.getBoundingClientRect();
      var thumbBox = btns[i].getBoundingClientRect();
      var behavior = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (thumbBox.left < stripBox.left) {
        strip.scrollBy({ left: thumbBox.left - stripBox.left - 6, behavior: behavior });
      } else if (thumbBox.right > stripBox.right) {
        strip.scrollBy({ left: thumbBox.right - stripBox.right + 6, behavior: behavior });
      }
    }

    function show(i, focusThumb) {
      i = (i + total) % total;
      current = i;
      imgs.forEach(function (im, n) {
        var active = n === i;
        im.classList.toggle('is-on', active);
        im.alt = active ? p.name + ', photo ' + (i + 1) : '';
      });
      btns.forEach(function (b, n) {
        var active = n === i;
        b.classList.toggle('is-on', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.tabIndex = active ? 0 : -1;
      });
      if (hint) {
        hint.textContent = (i + 1) + ' of ' + total;
        hint.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + total);
      }
      stage.setAttribute('aria-label', p.name + ' photos. Photo ' + (i + 1) + ' of ' + total);
      keepThumbVisible(i);
      if (focusThumb) btns[i].focus();
    }

    strip.addEventListener('click', function (e) {
      var tab = e.target.closest && e.target.closest('[role="tab"]');
      if (!tab) return;
      show(btns.indexOf(tab), false);
    });
    strip.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1, true); }
      else if (e.key === 'Home') { e.preventDefault(); show(0, true); }
      else if (e.key === 'End') { e.preventDefault(); show(total - 1, true); }
    });

    stage.setAttribute('role', 'group');
    stage.setAttribute('aria-roledescription', 'carousel');
    stage.setAttribute('aria-label', p.name + ' photos. Photo 1 of ' + total);
    stage.tabIndex = 0;
    if (hint) {
      hint.setAttribute('aria-live', 'polite');
      hint.setAttribute('aria-atomic', 'true');
    }

    [
      { direction: -1, name: 'Previous photo', className: 'gal__arrow gal__arrow--prev', path: 'm15 18-6-6 6-6' },
      { direction: 1, name: 'Next photo', className: 'gal__arrow gal__arrow--next', path: 'm9 18 6-6-6-6' }
    ].forEach(function (item) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = item.className;
      button.setAttribute('aria-label', item.name);
      button.setAttribute('aria-controls', 'stage');
      var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', item.path);
      icon.appendChild(path);
      button.appendChild(icon);
      button.addEventListener('click', function () { show(current + item.direction, false); });
      stage.appendChild(button);
    });

    var swipe = null;
    stage.addEventListener('pointerdown', function (e) {
      if ((e.button !== undefined && e.button !== 0) || (e.target.closest && e.target.closest('button'))) return;
      swipe = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, dy: 0, horizontal: false };
      if (stage.setPointerCapture) stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!swipe || swipe.id !== e.pointerId) return;
      swipe.dx = e.clientX - swipe.x;
      swipe.dy = e.clientY - swipe.y;
      if (Math.abs(swipe.dx) > 10 && Math.abs(swipe.dx) > Math.abs(swipe.dy) * 1.15) {
        swipe.horizontal = true;
        stage.classList.add('is-swiping');
      }
    });
    function finishSwipe(e) {
      if (!swipe || swipe.id !== e.pointerId) return;
      var gesture = swipe;
      swipe = null;
      stage.classList.remove('is-swiping');
      var threshold = Math.max(36, stage.getBoundingClientRect().width * 0.085);
      if (gesture.horizontal && Math.abs(gesture.dx) >= threshold) show(current + (gesture.dx < 0 ? 1 : -1), false);
    }
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (eventName) {
      stage.addEventListener(eventName, finishSwipe);
    });
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1, false); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1, false); }
      else if (e.key === 'Home') { e.preventDefault(); show(0, false); }
      else if (e.key === 'End') { e.preventDefault(); show(total - 1, false); }
    });

    var help = document.createElement('p');
    help.className = 'gal__help';
    help.textContent = 'Swipe to explore · tap a photo below';
    stage.parentNode.insertBefore(strip, stage.nextSibling);
    stage.parentNode.insertBefore(help, strip.nextSibling);
    show(0, false);
  }
  function viewer(p) {
    var stage = $('#stage');
    var hint = $('#hint');
    var fillBar = $('#spinFill');
    var note = $('#spinNote');
    /* A gallery is not a spin. The machines carry frames of one object turned
       a few degrees at a time, which is why dragging works on them. A tool kit
       carries unrelated photographs — the case, the bit list, the ratchet
       close up, somebody using it on a wheel — and dragging between those
       would read as a broken turntable. So a `gallery` gets thumbnails and
       leaves the rotation code alone. */
    var srcs = p.gallery ? frameList(p.frames).slice(0, 1).concat(p.gallery)
                         : frameList(p.frames);
    var N = srcs.length;
    var imgs = [];

    /* Each frame is a <picture>: WebP for the browsers that take it, the same
       JPEG for the ones that don't. The viewer keeps working on the <img>
       inside, so none of the spin logic below knows the difference. */
    srcs.forEach(function (src, i) {
      var pic = window.IFZPic(src, i === 0 ? p.name : '');
      var img = pic.querySelector('img');
      img.draggable = false;
      if (i === 0) img.className = 'is-on';
      stage.appendChild(pic);
      imgs.push(img);
    });


    if (p.gallery || N <= 1) {
      /* Neither a gallery nor a lone photograph can be spun, so the rotation
         controls go rather than sit there refusing to move — and the old copy
         promised "more angles coming soon", which is a promise nobody is
         going to keep for a $5 socket set. */
      hint.textContent = p.photoLabel || 'Photo of the real machine';
      var spin = $('.spin');
      if (spin && spin.parentNode) spin.parentNode.removeChild(spin);
      if (p.gallery) thumbs(stage, imgs, srcs, hint, p);
      return;
    }

    note.textContent = 'Drag to spin · real photos, ' + N + ' angles';

    var pos = 0;          /* continuous rotation, 0 .. N-1 */
    var shown = 0;
    var vel = 0;
    var raf = null;
    var mode = 'idle';    /* idle | drag | inertia | anim */
    var animFrom = 0, animTo = 0, animT0 = 0, animDur = 0;

    function render() {
      var idx = Math.round(Math.min(N - 1, Math.max(0, pos)));
      if (idx !== shown) {
        imgs[shown].classList.remove('is-on');
        imgs[idx].classList.add('is-on');
        shown = idx;
        fillBar.style.width = (((idx + 1) / N) * 100).toFixed(1) + '%';
      }
    }

    function loop(ts) {
      if (mode === 'inertia') {
        pos += vel;
        vel *= 0.90;
        if (pos < 0)     { pos = 0;     vel = 0; }
        if (pos > N - 1) { pos = N - 1; vel = 0; }
        render();
        if (Math.abs(vel) < 0.012) {
          mode = 'anim';
          animFrom = pos; animTo = Math.round(pos);
          animT0 = ts; animDur = 140;
        }
      } else if (mode === 'anim') {
        var t = Math.min(1, (ts - animT0) / animDur);
        var e = 1 - Math.pow(1 - t, 3);
        pos = animFrom + (animTo - animFrom) * e;
        render();
        if (t >= 1) { pos = animTo; mode = 'idle'; }
      }
      if (mode !== 'idle') { raf = requestAnimationFrame(loop); }
      else raf = null;
    }

    function run() { if (!raf) raf = requestAnimationFrame(loop); }

    function animateTo(target, dur) {
      animFrom = pos;
      animTo = Math.min(N - 1, Math.max(0, target));
      animT0 = performance.now();
      animDur = dur || 240;
      mode = 'anim';
      run();
    }

    var hintGone = false;
    function dismissHint() {
      if (hintGone) return;
      hintGone = true;
      hint.classList.add('is-gone');
    }

    $('#spinL').addEventListener('click', function () { dismissHint(); animateTo(Math.round(pos) - 1); });
    $('#spinR').addEventListener('click', function () { dismissHint(); animateTo(Math.round(pos) + 1); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { dismissHint(); animateTo(Math.round(pos) - 1); }
      if (e.key === 'ArrowRight') { dismissHint(); animateTo(Math.round(pos) + 1); }
    });

    /* Drag: the full stage width sweeps the full arc, like real turntables.
       Velocity is tracked for flick inertia on release. */
    var dragging = false, lastX = 0, lastT = 0;

    stage.addEventListener('pointerdown', function (e) {
      dragging = true;
      lastX = e.clientX; lastT = performance.now();
      vel = 0; mode = 'drag';
      stage.classList.add('is-dragging');
      stage.setPointerCapture(e.pointerId);
      dismissHint();
    });

    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var pxPerFrame = stage.getBoundingClientRect().width / (N + 1);
      var now = performance.now();
      var dx = e.clientX - lastX;
      var dpos = dx / pxPerFrame;
      pos = Math.min(N - 1 + 0.45, Math.max(-0.45, pos + dpos));
      vel = dpos * Math.min(1, 16 / Math.max(1, now - lastT));
      lastX = e.clientX; lastT = now;
      render();
    });

    ['pointerup', 'pointercancel'].forEach(function (t) {
      stage.addEventListener(t, function () {
        if (!dragging) return;
        dragging = false;
        stage.classList.remove('is-dragging');
        mode = 'inertia';
        run();
      });
    });

    /* Intro: wait for every frame to decode, then one eased sweep out and
       back so the page opens moving. Skipped the moment the user touches. */
    Promise.all(imgs.map(function (im) {
      return im.decode ? im.decode().catch(function () {}) : Promise.resolve();
    })).then(function () {
      if (hintGone) return;
      animateTo(N - 1, 900);
      setTimeout(function () { if (!hintGone) animateTo(0, 900); }, 1050);
    });
  }


  /* ------------------------------------------------------------------
     3D model — the viewer itself lives in ifz-3d.js, shared with the
     package pages so there is one copy of the maths, not two.
     ------------------------------------------------------------------ */
  /* Returns true when the model has taken the stage, because the caller must
     then NOT start the spin viewer: it binds to #spinL and #spinR, and those
     have gone with the photographs.

     Only the three machines have a model. The bench, the bar and the plates
     have no `spec` and keep their ring of photographs exactly as before. */
  function build3d(p) {
    if (!window.IFZ3D || !window.IFZ3D.inline) return false;
    var spec = window.IFZ3D.spec(key());
    if (!spec) return false;

    var stage = $('#stage');
    if (!stage) return false;
    spec.name = p.name;

    /* The model replaces the photographs rather than sitting under them. It
       turns to any angle instead of to one of five, it zooms, and it is the
       same file the full-screen stage uses — so opening that afterwards
       downloads nothing new. */
    stage.innerHTML = '';
    stage.classList.add('stage--3d');

    /* Turn left / Turn right drove the frame ring. There is no ring now, and
       the model is turned by dragging it. */
    var spin = $('.spin');
    if (spin && spin.parentNode) spin.parentNode.removeChild(spin);

    window.IFZ3D.inline(spec, stage);
    return true;
  }


  /* ------------------------------------------------------------------
     Add to cart
     ------------------------------------------------------------------ */
  function wireCart(p) {
    var btn = document.getElementById('addCart');
    if (!btn) return;
    var added = false;

    /* ------------------------------------------------------------------
       ORDERING, CASH ON DELIVERY

       "Order on WhatsApp" is now "Order now — cash on delivery", and it
       goes through the cart rather than opening a chat.

       Why the cart and not EasySell's own product-page button: this page
       lets people tick a bench, a bar and a weight of plates.
       EasySell's product button orders the PRODUCT — it knows nothing about
       those add-ons and would quietly drop every one of them. Putting the
       whole selection in the cart first and handing EasySell the cart is
       the only route that orders what was actually chosen.
       ------------------------------------------------------------------ */
    function add() {

      /* Plates are sold by weight, so the cart line is the weight itself
         rather than a $3 unit with the kilos bolted on as an extra. */
      if (p.perKg) {
        if (state.plates <= 0) {
          btn.classList.add('is-warn');
          btn.textContent = 'Pick a weight first';
          setTimeout(function () {
            btn.classList.remove('is-warn');
            btn.textContent = 'Add to cart';
          }, 1600);
          return false;
        }
        window.IFZCart.add({
          type: 'accessory',
          name: state.plates + ' kg Rubber Olympic Plates',
          price: state.plates * p.price,
          img: thumbOf(p),
          /* Shopify sells plates as individual 2.5/5/10/20 kg pieces, so the
             weight is carried as `kg` and split into pieces at order time. */
          key: 'plates', kg: state.plates,
          extras: [],
          includes: p.included
        });
      } else if (p.perM2) {
        /* Same shape as plates: the line IS the area, not a $39 unit with the
           metres bolted on. `m2` is the quantity Shopify is asked for. */
        if (state.floor <= 0) {
          btn.classList.add('is-warn');
          btn.textContent = 'Pick an area first';
          setTimeout(function () {
            btn.classList.remove('is-warn');
            btn.textContent = 'Add to cart';
          }, 1600);
          return false;
        }
        window.IFZCart.add({
          type: 'accessory',
          name: state.floor + ' m² Rubber Gym Flooring — 15 mm',
          price: state.floor * p.price,
          img: thumbOf(p),
          key: 'floor', m2: state.floor,
          extras: [],
          includes: p.included
        });
      } else {
        var extras = [];
        p.addons.forEach(function (k) {
          /* `key` is what the order worker resolves to a Shopify variant.
             Every add-on becomes its own line, because that is what it is. */
          if (state.picked[k]) extras.push({ key: k, name: ADDONS[k].name, price: ADDONS[k].price });
        });
        if (state.plates > 0) {
          extras.push({ key: 'plates', kg: state.plates,
                        name: 'Rubber plates — ' + state.plates + ' kg',
                        price: state.plates * PLATE_PER_KG });
        }
        if (state.floor > 0) {
          extras.push({ key: 'floor', m2: state.floor,
                        name: 'Rubber flooring — ' + state.floor + ' m² (' + (state.floor * 4) + ' tiles)',
                        price: state.floor * FLOOR_PER_M2 });
        }

        window.IFZCart.add({
          /* cartType because the tool kits are accessories as far as this
             page is concerned but their own thing in the cart — without it
             the same socket set reads "Tool set" when it is added from the
             tools page and "Accessory" when it is added from its own. */
          type: p.cartType || (p.accessory ? 'accessory' : 'machine'),
          name: p.name,
          price: p.price,
          img: thumbOf(p),
          key: key(),
          extras: extras,
          includes: p.included
        });
      }

      added = true;
      btn.classList.add('is-added');
      btn.textContent = '✓ Added! View cart →';
      return true;
    }

    btn.addEventListener('click', function () {
      if (added) { window.location.href = window.IFZU('cart-revamp.html'); return; }
      add();
    });

  }

  var p = PRODUCTS[key()];

  /* Shopify is the authority on price — it is what will actually be charged.
     Quoting anything else here is how a customer ends up arguing at the door,
     so the theme's price wins over the one in the table above. */
  if (window.IFZ_PRODUCT_PRICE) {
    var shopPrice = parseFloat(window.IFZ_PRODUCT_PRICE);
    if (shopPrice > 0) p.price = shopPrice;
  }
  fill(p);
  buildExtras(p);
  wireCart(p);
  var took3d = build3d(p);
  buyBar(p);          /* before the first refresh, so the bar starts in sync */
  refreshOrder(p);
  /* The spin viewer binds to #spinL and #spinR. On a machine those are gone
     with the photographs, so it is not started at all — which also means its
     frame ring is never downloaded. */
  if (!took3d) viewer(p);
})();
